import React, { useState } from 'react';
import { Box, Container, Typography, Card, CardContent, Button, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import SpeedIcon from '@mui/icons-material/Speed';
import BiotechIcon from '@mui/icons-material/Biotech';
import MouseIcon from '@mui/icons-material/Mouse';
import { Link as ScrollLink } from 'react-scroll';

const SolutionSection: React.FC = () => {
  const theme = useTheme();
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const solutionFeatures = [
    {
      icon: <BiotechIcon sx={{ fontSize: 50 }} />,
      title: 'Анализ на основе ИИ',
      description: 'Наша нейронная сеть изучает поведение игроков и выявляет подозрительные действия с высокой точностью.',
      color: theme.palette.primary.main,
    },
    {
      icon: <MouseIcon sx={{ fontSize: 50 }} />,
      title: 'Простая интеграция',
      description: 'Интегрируйте решение в свою игру буквально за один клик через встроенное меню Unity.',
      color: theme.palette.primary.main,
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 50 }} />,
      title: 'Универсальное обнаружение',
      description: 'Автоматически определяет спид-хаки, изменение времени и другие методы нечестной игры без дополнительных настроек.',
      color: theme.palette.primary.main,
    },
    {
      icon: <AlarmOffIcon sx={{ fontSize: 50 }} />,
      title: 'Минимальное влияние на производительность',
      description: 'Оптимизированная система работает в фоновом режиме, практически не потребляя ресурсы вашей игры.',
      color: theme.palette.primary.main,
    },
  ];

  return (
    <Box
      id="solution"
      sx={{
        py: 12,
        background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, #fff 100%)`,
      }}
    >
      <Container>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              component="h2"
              color="primary"
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Решение: GameGuard AI
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary', maxWidth: '800px', mx: 'auto' }}>
              GameGuard AI — революционная система защиты от читеров, созданная специально для игр на Unity.
              Мы предлагаем эффективную защиту корпоративного уровня по доступной цене.
            </Typography>
          </motion.div>
        </Box>

        <Box sx={{ mb: 10 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: 4,
            }}
          >
            {solutionFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '5px',
                      background: feature.color,
                      opacity: hoveredCard === index ? 1 : 0.7,
                      transition: 'opacity 0.3s ease',
                    }}
                  />
                  <CardContent
                    sx={{
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 70,
                        height: 70,
                        borderRadius: '12px',
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        color: feature.color,
                        mb: 3,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ flex: 1 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        </Box>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Box
            sx={{
              p: 5,
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              borderRadius: 4,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Box sx={{ flex: 2 }}>
              <Typography variant="h3" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                Почему разработчики выбирают GameGuard AI
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Наше интеллектуальное решение обеспечивает быструю окупаемость инвестиций, устраняет необходимость в ручных проверках,
                способствует удержанию игроков и позволяет вам сфокусироваться на создании качественного игрового опыта.
              </Typography>
              <Box
                component="ul"
                sx={{ 
                  pl: 2,
                  '& li': { 
                    mb: 1,
                    color: 'text.primary'
                  }
                }}
              >
                <Typography component="li" variant="body1">
                  <strong>Быстрая окупаемость:</strong> Экономия до 150 000 рублей ежемесячно на ручных проверках и модерации сообщений о читерах
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Повышение лояльности игроков:</strong> Создайте справедливую игровую среду и сохраните честных игроков в вашем проекте
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Экономия ресурсов разработки:</strong> Сконцентрируйтесь на улучшении игры, а не на борьбе с читерами
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Самообучающийся алгоритм:</strong> Система непрерывно совершенствуется, адаптируясь к новым типам читов
                </Typography>
                <Typography component="li" variant="body1">
                  <strong>Готовое решение:</strong> Предварительно обученные модели сразу определяют популярные читы без дополнительной настройки
                </Typography>
              </Box>
            </Box>
            <Box 
              sx={{ 
                flex: 1, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center'
              }}
            >
              <motion.div
                whileHover={{ rotate: 5, scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <ScrollLink to="faq" smooth={true} duration={500}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      borderRadius: 3,
                      py: 2,
                      px: 4,
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #1565c0 30%, #2196f3 90%)',
                      boxShadow: '0 8px 40px rgba(33, 150, 243, 0.4)',
                    }}
                  >
                    Начать использовать
                  </Button>
                </ScrollLink>
              </motion.div>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default SolutionSection; 